import React from "react";
import { Autocomplete, Box, TextField, Typography, createFilterOptions } from "@mui/material";

import { IPD_COLORS, ipdElevatedInputSx } from "../../ipd/utils";

// "cardio" should find the cardiologist, not only a doctor named Cardio.
const filterDoctors = createFilterOptions({
  stringify: (d) => `${d.name || ""} ${d.specialization || ""}`,
});

/**
 * Type-to-search picker for the doctor named as Emergency Provider.
 *
 * Searchable because a facility's doctor list is too long to scroll through at
 * the bedside. It also replaces a plain select whose menu grew to the width of
 * the longest "name — specialisation" option and spilled out of the narrow
 * dialog: this list is held to the field's own width, and a long
 * specialisation wraps under the name instead of widening it.
 *
 * `doctors` are { id, name, specialization }; `value` and `onChange` deal in
 * the doctor's id, the same as the select this replaces.
 */
export default function EmergencyDoctorAutocomplete({
  doctors,
  value,
  onChange,
  placeholder,
  helperText,
  ariaLabel,
}) {
  const selected = doctors.find((d) => d.id === value) || null;

  return (
    <Autocomplete
      size="small"
      options={doctors}
      value={selected}
      onChange={(_, d) => onChange(d ? d.id : "")}
      filterOptions={filterDoctors}
      getOptionLabel={(d) => d?.name || ""}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      noOptionsText="No doctor matches"
      renderOption={(props, d) => (
        <li {...props} key={d.id}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{d.name}</Typography>
            {d.specialization && (
              <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                {d.specialization}
              </Typography>
            )}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          helperText={helperText}
          sx={ipdElevatedInputSx}
          inputProps={{ ...params.inputProps, "aria-label": ariaLabel }}
        />
      )}
    />
  );
}
